import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { useLocation } from "react-router";
import type { UserType } from "@/types/account";

type ChatMessage = {
    id: string;
    role: "user" | "assistant";
    content: string;
    timestamp: number;
};

type Point = { x: number; y: number };

export type ChatHistory = {
    id: string;
    timestamp: number;
    messages: ChatMessage[];
    formData?: Record<string, any>;
    completed?: boolean;
    wizardType?: "container" | "order" | "quote_request" | "quote_response";
    quoteRequestId?: string; // Per FREIGHT_FORWARDER
    title?: string; // Titolo personalizzato della chat
};

type ChatAIContextValue = {
    isDrawerOpen: boolean;
    openDrawer: () => void;
    closeDrawer: () => void;
    toggleDrawer: () => void;

    isPromptOpen: boolean;
    promptPosition: Point | null;
    openFloatingPrompt: (pos: Point) => void;
    closeFloatingPrompt: () => void;

    messages: ChatMessage[];
    appendMessage: (m: Omit<ChatMessage, "id" | "timestamp"> & Partial<Pick<ChatMessage, "timestamp">>) => void;
    submitPrompt: (text: string) => void;

    // typing state for assistant placeholder
    isTyping: boolean;
    startTyping: () => void;
    stopTyping: () => void;

    // Chat history
    currentChatId: string | null;
    isNewChat: boolean;
    createNewChat: () => void;
    saveChatHistory: () => void;
    loadLastChat: () => ChatHistory | null;
    getAllChats: () => ChatHistory[];
    loadChat: (chatId: string) => void;
    deleteChat: (chatId: string) => void;
    updateChatTitle: (chatId: string, title: string) => void;
    
    // History view
    isHistoryView: boolean;
    toggleHistoryView: () => void;

    // Wizard state
    activeWizard: "container" | "order" | "quote_request" | "quote_response" | null;
    setActiveWizard: (wizard: "container" | "order" | "quote_request" | "quote_response" | null) => void;
    wizardFormData: Record<string, any>;
    setWizardFormData: (data: Record<string, any>) => void;

    // Quote context for FREIGHT_FORWARDER
    quoteRequestId: string | null;
    setQuoteRequestId: (id: string | null) => void;
};

const ChatAIContext = createContext<ChatAIContextValue | null>(null);

export const useChatAI = (): ChatAIContextValue => {
    const ctx = useContext(ChatAIContext);
    if (!ctx) throw new Error("useChatAI must be used within ChatAIProvider");
    return ctx;
};

function generateId(): string {
    // simple unique id for UI purposes
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getStorageKey(): string {
    const accountId = localStorage.getItem('accountId') || '1';
    const userType = localStorage.getItem('userType') as UserType | null || 'SHIPPER';
    return `aiChatHistory_${accountId}_${userType}`;
}

function loadChatHistory(): ChatHistory[] {
    try {
        const key = getStorageKey();
        const stored = localStorage.getItem(key);
        if (stored) {
            const parsed = JSON.parse(stored);
            // Convert timestamp dates back to Date objects
            return parsed.map((chat: any) => ({
                ...chat,
                timestamp: new Date(chat.timestamp),
                messages: chat.messages.map((msg: any) => ({
                    ...msg,
                    timestamp: new Date(msg.timestamp),
                })),
            }));
        }
    } catch (error) {
        console.error('Error loading chat history:', error);
    }
    return [];
}

function saveChatHistory(history: ChatHistory[]): void {
    try {
        const key = getStorageKey();
        localStorage.setItem(key, JSON.stringify(history));
    } catch (error) {
        console.error('Error saving chat history:', error);
    }
}

export const ChatAIProvider = ({ children }: PropsWithChildren) => {
    const location = useLocation();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isPromptOpen, setIsPromptOpen] = useState(false);
    const [promptPosition, setPromptPosition] = useState<Point | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [currentChatId, setCurrentChatId] = useState<string | null>(null);
    const [activeWizard, setActiveWizard] = useState<"container" | "order" | "quote_request" | "quote_response" | null>(null);
    const [wizardFormData, setWizardFormData] = useState<Record<string, any>>({});
    const [quoteRequestId, setQuoteRequestId] = useState<string | null>(null);
    const [isHistoryView, setIsHistoryView] = useState(false);
    const [historyVersion, setHistoryVersion] = useState(0); // Force re-render when history changes

    // Track last mouse position to place floating prompt near cursor
    const lastCursor = useRef<Point>({ x: window.innerWidth / 2, y: window.innerHeight / 2 });

    const isSenderOrReceiver = useMemo(() => {
        const p = location.pathname;
        return p.startsWith("/sender") || p.startsWith("/receiver");
    }, [location.pathname]);

    const createNewChat = useCallback(() => {
        const newChatId = generateId();
        setCurrentChatId(newChatId);
        setMessages([]);
        setActiveWizard(null);
        setWizardFormData({});
        setQuoteRequestId(null);
        setIsTyping(false);
        setIsHistoryView(false); // Torna alla vista chat quando si crea una nuova chat
    }, []);

    const loadLastChat = useCallback((): ChatHistory | null => {
        const history = loadChatHistory();
        if (history.length === 0) return null;
        // Get most recent chat
        const lastChat = history.sort((a: ChatHistory, b: ChatHistory) => b.timestamp - a.timestamp)[0];
        return lastChat;
    }, []);

    const saveChatHistoryToStorage = useCallback(() => {
        if (!currentChatId) return;
        const history = loadChatHistory();
        const existingIndex = history.findIndex((c) => c.id === currentChatId);
        const chatData: ChatHistory = {
            id: currentChatId,
            timestamp: Date.now(),
            messages,
            formData: wizardFormData,
            completed: activeWizard === null && messages.length > 0,
            wizardType: activeWizard || undefined,
            quoteRequestId: quoteRequestId || undefined,
        };
        if (existingIndex !== -1) {
            history[existingIndex] = chatData;
        } else {
            history.push(chatData);
        }
        // Keep only last 50 chats
        const sorted = history.sort((a: ChatHistory, b: ChatHistory) => b.timestamp - a.timestamp).slice(0, 50);
        saveChatHistory(sorted);
    }, [currentChatId, messages, wizardFormData, activeWizard, quoteRequestId]);

    const getAllChats = useCallback((): ChatHistory[] => {
        const history = loadChatHistory();
        // Sort by timestamp, most recent first
        return history.sort((a: ChatHistory, b: ChatHistory) => b.timestamp - a.timestamp);
    }, [historyVersion]); // Re-run when historyVersion changes

    const loadChat = useCallback((chatId: string) => {
        // Save current chat before loading new one
        saveChatHistoryToStorage();
        
        const history = loadChatHistory();
        const chat = history.find((c) => c.id === chatId);
        
        if (chat) {
            setCurrentChatId(chat.id);
            setMessages(chat.messages);
            setActiveWizard(chat.wizardType || null);
            setWizardFormData(chat.formData || {});
            setQuoteRequestId(chat.quoteRequestId || null);
            setIsTyping(false);
            setIsHistoryView(false); // Torna alla vista chat quando si carica una chat
        } else {
            // If chat not found, create new chat
            createNewChat();
        }
    }, [saveChatHistoryToStorage, createNewChat]);

    const deleteChat = useCallback((chatId: string) => {
        // Save current chat before deleting
        saveChatHistoryToStorage();
        
        const history = loadChatHistory();
        const filtered = history.filter((c) => c.id !== chatId);
        saveChatHistory(filtered);
        setHistoryVersion((prev) => prev + 1); // Trigger re-render
        
        // If deleted chat was current, create new chat or load last one
        if (currentChatId === chatId) {
            const remaining = filtered.sort((a: ChatHistory, b: ChatHistory) => b.timestamp - a.timestamp);
            if (remaining.length > 0) {
                const lastChat = remaining[0];
                setCurrentChatId(lastChat.id);
                setMessages(lastChat.messages);
                setActiveWizard(lastChat.wizardType || null);
                setWizardFormData(lastChat.formData || {});
                setQuoteRequestId(lastChat.quoteRequestId || null);
                setIsTyping(false);
            } else {
                createNewChat();
            }
        }
    }, [currentChatId, createNewChat, saveChatHistoryToStorage]);

    const updateChatTitle = useCallback((chatId: string, title: string) => {
        const history = loadChatHistory();
        const chatIndex = history.findIndex((c) => c.id === chatId);
        if (chatIndex !== -1) {
            history[chatIndex] = {
                ...history[chatIndex],
                title: title.trim() || undefined,
            };
            saveChatHistory(history);
            setHistoryVersion((prev) => prev + 1); // Trigger re-render
        }
    }, []);

    const toggleHistoryView = useCallback(() => {
        setIsHistoryView((prev) => !prev);
    }, []);

    const openDrawer = useCallback(() => {
        setIsDrawerOpen(true);
        setIsHistoryView(false); // Di default mostra la chat, non la history
        // Load last chat if exists, otherwise create new
        const lastChat = loadLastChat();
        if (lastChat && lastChat.messages.length > 0) {
            setCurrentChatId(lastChat.id);
            setMessages(lastChat.messages);
            setActiveWizard(lastChat.wizardType || null);
            setWizardFormData(lastChat.formData || {});
            setQuoteRequestId(lastChat.quoteRequestId || null);
        } else {
            createNewChat();
        }
    }, [loadLastChat, createNewChat]);

    const closeDrawer = useCallback(() => {
        saveChatHistoryToStorage();
        setIsDrawerOpen(false);
    }, [saveChatHistoryToStorage]);

    const toggleDrawer = useCallback(() => {
        if (isDrawerOpen) {
            closeDrawer();
        } else {
            openDrawer();
        }
    }, [isDrawerOpen, openDrawer, closeDrawer]);

    const openFloatingPrompt = useCallback((pos: Point) => {
        setPromptPosition(pos);
        setIsPromptOpen(true);
    }, []);
    const closeFloatingPrompt = useCallback(() => setIsPromptOpen(false), []);

    const appendMessage = useCallback((m: Omit<ChatMessage, "id" | "timestamp"> & Partial<Pick<ChatMessage, "timestamp">>) => {
        setMessages((prev) => {
            const newMessage = {
                id: generateId(),
                timestamp: m.timestamp ?? Date.now(),
                role: m.role,
                content: m.content,
            };
            // Auto-save after adding message
            setTimeout(() => {
                const history = loadChatHistory();
                if (currentChatId) {
                    const chatIndex = history.findIndex((c) => c.id === currentChatId);
                    if (chatIndex !== -1) {
                        history[chatIndex] = {
                            ...history[chatIndex],
                            messages: [...prev, newMessage],
                            formData: wizardFormData,
                            wizardType: activeWizard || undefined,
                            quoteRequestId: quoteRequestId || undefined,
                        };
                    } else {
                        history.push({
                            id: currentChatId,
                            timestamp: Date.now(),
                            messages: [...prev, newMessage],
                            formData: wizardFormData,
                            wizardType: activeWizard || undefined,
                            quoteRequestId: quoteRequestId || undefined,
                        });
                    }
                    saveChatHistory(history);
                }
            }, 0);
            return [...prev, newMessage];
        });
    }, [currentChatId, wizardFormData, activeWizard, quoteRequestId]);

    const submitPrompt = useCallback(
        (text: string) => {
            if (!text.trim()) return;
            setIsPromptOpen(false);
            setIsDrawerOpen(true);
            appendMessage({ role: "user", content: text });
            setIsTyping(true);
        },
        [appendMessage],
    );

    useEffect(() => {
        if (!isSenderOrReceiver) return;

        const onMouseMove = (e: MouseEvent) => {
            lastCursor.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener("mousemove", onMouseMove, { passive: true });

        const isAIShortcut = (e: KeyboardEvent) => (e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "a";
        const onKeyDown = (e: KeyboardEvent) => {
            if (isAIShortcut(e)) {
                e.preventDefault();
                const { x, y } = lastCursor.current;
                openFloatingPrompt({ x, y });
            }
        };
        window.addEventListener("keydown", onKeyDown);

        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [isSenderOrReceiver, openFloatingPrompt]);

    const isNewChat = useMemo(() => {
        return messages.length === 0 && !activeWizard;
    }, [messages.length, activeWizard]);

    // Auto-save on unmount or when drawer closes
    useEffect(() => {
        return () => {
            if (currentChatId) {
                saveChatHistoryToStorage();
            }
        };
    }, [currentChatId, saveChatHistoryToStorage]);

    const value = useMemo<ChatAIContextValue>(
        () => ({
            isDrawerOpen,
            openDrawer,
            closeDrawer,
            toggleDrawer,
            isPromptOpen,
            promptPosition,
            openFloatingPrompt,
            closeFloatingPrompt,
            messages,
            appendMessage,
            submitPrompt,
            isTyping,
            startTyping: () => setIsTyping(true),
            stopTyping: () => setIsTyping(false),
            currentChatId,
            isNewChat,
            createNewChat,
            saveChatHistory: saveChatHistoryToStorage,
            loadLastChat,
            getAllChats,
            loadChat,
            deleteChat,
            updateChatTitle,
            isHistoryView,
            toggleHistoryView,
            activeWizard,
            setActiveWizard,
            wizardFormData,
            setWizardFormData,
            quoteRequestId,
            setQuoteRequestId,
        }),
        [
            appendMessage,
            closeDrawer,
            closeFloatingPrompt,
            createNewChat,
            currentChatId,
            deleteChat,
            getAllChats,
            isDrawerOpen,
            isHistoryView,
            isNewChat,
            isPromptOpen,
            isTyping,
            loadChat,
            loadLastChat,
            messages,
            openDrawer,
            openFloatingPrompt,
            promptPosition,
            quoteRequestId,
            saveChatHistoryToStorage,
            submitPrompt,
            toggleDrawer,
            toggleHistoryView,
            updateChatTitle,
            activeWizard,
            wizardFormData,
        ],
    );

    return <ChatAIContext.Provider value={value}>{children}</ChatAIContext.Provider>;
};

// Optional portals (drawer and prompt) will be mounted in main.tsx for clarity


